import React, { forwardRef } from 'react';

import { useIntl } from 'react-intl-next';

import Badge, { type BadgeProps } from '@atlaskit/badge';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	MAX_MORE_TAB_COUNT,
	MORE_TRIGGER_ID,
	UNSAFE_HORIZONTAL_NAVIGATION_COLOR_BACKGROUND_NEUTRAL_VAR,
	UNSAFE_INTERNAL_MORE_COLOR_VAR,
} from '@atlassian/navigation-kit-common';
import type { TabProps } from '@atlassian/navigation-kit-common/src/types';
import Tab, { TabProvider } from '@atlassian/navigation-kit-ui-tab';

import messages from './messages';

const ThemedBadge = (props: Omit<BadgeProps, 'style' | 'appearance'>) => {
	return (
		<Badge
			{...props}
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				color: `var(${UNSAFE_INTERNAL_MORE_COLOR_VAR}, ${token('color.text.subtle')})`,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				backgroundColor: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_BACKGROUND_NEUTRAL_VAR}, ${token('color.background.neutral')})`,
			}}
		/>
	);
};

export const MoreTrigger = forwardRef(
	(
		{
			count,
			...rest
		}: Omit<TabProps, 'type' | 'name' | 'id'> & { count: number; isOpen?: boolean },
		ref: React.Ref<HTMLElement>,
	) => {
		const { formatMessage } = useIntl();
		return (
			<TabProvider id={MORE_TRIGGER_ID} isOverflowing={false} isSelected={false}>
				<Tab
					{...rest}
					ref={ref}
					name={formatMessage(messages.moreTabWithBadge)}
					type="dropdown-trigger"
					isMoreMenu
					isChevronHidden
					testId="navigation-kit-ui-tab-list.ui.more-trigger.more-tab"
					elemAfter={
						<>
							{' '}
							{
								// no wrapper is required for count > 9 as the badge will always contain 9+ so will be the same width
								count > MAX_MORE_TAB_COUNT ? (
									<ThemedBadge max={MAX_MORE_TAB_COUNT}>{count}</ThemedBadge>
								) : (
									<Box as="span" xcss={moreDigitBadgeWrapperStyles}>
										<ThemedBadge max={MAX_MORE_TAB_COUNT}>{count}</ThemedBadge>
									</Box>
								)
							}
						</>
					}
				/>
			</TabProvider>
		);
	},
);

const moreDigitBadgeWrapperStyles = xcss({ display: 'flex', minWidth: token('space.250', '20px') });
